import axios, { AxiosProgressEvent } from 'axios';
import { call, put, select, takeEvery } from 'redux-saga/effects';

import { ActivityController } from '@/controllers/Activity';
import { UploadsController } from '@/controllers/Uploads';
import { ROUTES } from '@/router';
import { Navigation } from '@/services/Navigation';
import { store } from '@/store';
import { SurveyActionTypes } from '@/store/reducers/site-survey/action-types';
import { SiteSurveyStep } from '@/store/reducers/site-survey/types';
import { AppState, ReduxAction, ReduxSagaActionFunc } from '@/types';

import { SiteSurveyController } from './SiteSurveyController';
import { UploadFileSurvey } from './types';

export function* fetchSurveyActivityData(action: ReduxAction) {
  const { activityId } = action.sagaPayload;

  try {
    const activityResponse = yield call(ActivityController.fetchActivity, activityId);
    const siteSurveyResponse = yield call(SiteSurveyController.loadSurvey, activityId);

    yield put({
      type: SurveyActionTypes.FETCH_SURVEY_ACTIVITY_INFO.SUCCESS,
      payload: {
        activity: activityResponse.data,
        siteSurvey: siteSurveyResponse.data,
      },
    });
  } catch (error) {
    yield put({ type: SurveyActionTypes.FETCH_SURVEY_ACTIVITY_INFO.FAIL, error });

    if (axios.isAxiosError(error)) {
      const status = error.response?.status;

      // 400 happens if user sends an invalid ID by manually typing it in the URL
      if (status === 404 || status === 400) {
        Navigation.goTo(ROUTES.Public.notFound);
      } else if (status === 403) {
        Navigation.goTo(ROUTES.Private.Dashboard.ActivityList);
      } else {
        console.error(error);
      }
    }
  }
}

function* handleUploadError(errorMessage: string) {
  yield put({ type: SurveyActionTypes.UPLOAD_PHOTO_FAILED, error: errorMessage });
}

export function* uploadSiteFile(action: ReduxSagaActionFunc) {
  const promise = action.promise;
  const upload: UploadFileSurvey = action.sagaPayload.upload;

  const validFileTypes = ['image/jpg', 'image/jpeg', 'image/png', 'image/heic'];
  const fileType = upload.file.type;
  const fileSize = (upload.file.size / (1024 * 1024)).toFixed(2);
  const fileName = upload.file.name;

  try {
    if (!validFileTypes.includes(fileType)) {
      yield call(handleUploadError, 'Please upload a .jpg .png or .HIEC');

      return;
    }

    if (Number(fileSize) > 5) {
      yield call(handleUploadError, 'Please upload an image under 5MB');

      return;
    }

    const activityId = yield select(state => state.survey.activity.id);
    const result = yield call(promise, activityId, upload, function (evt: AxiosProgressEvent) {
      const percent = evt.progress || 0;
      const progressFix = Math.min(percent, 1);

      store.dispatch({
        type: SurveyActionTypes.UPLOAD_SURVEY_PROGRESS,
        payload: progressFix,
      });
    });
    const subStepId = upload.subStepId;

    if (subStepId) {
      const stepToOverride: SiteSurveyStep = yield select((state: AppState) =>
        state.survey.surveyState.find(step => step.id === subStepId),
      );

      if (stepToOverride) {
        yield UploadsController.deleteFile({
          activityId,
          bucketId: stepToOverride.gcsBucketId,
          fileId: stepToOverride.gcsItemId as string,
        });
      }
    }

    yield put({
      type: SurveyActionTypes.UPLOAD_SURVEY_FILE_DONE,
      payload: {
        data: result.data,
        stepNumber: upload.stepNumber,
        allowAdditionalFiles: upload.allowAdditionalFiles,
        subStepId,
        uploadedStepInfo: { name: fileName, size: fileSize },
      },
    });
  } catch (error) {
    if (axios.isCancel(error)) {
      yield put({ type: SurveyActionTypes.UPLOAD_SURVEY_FILE_CANCEL });

      return;
    }
    yield put({ type: SurveyActionTypes.UPLOAD_SURVEY_FILE_FAIL, error });
  } finally {
    URL.revokeObjectURL(action.payload);
  }
}

export function* saveAdditionaSurveylTask(action: ReduxAction) {
  const { stepPayload } = action.sagaPayload;

  try {
    const activityId = yield select(state => state.survey.activity.id);

    yield SiteSurveyController.saveAdditionaSurveylTask(activityId, stepPayload);

    yield put({
      type: SurveyActionTypes.SAVE_ADDITIONAL_SURVEY_TASK.SUCCESS,
      payload: stepPayload,
    });
  } catch (error) {
    yield put({ type: SurveyActionTypes.SAVE_ADDITIONAL_SURVEY_TASK.FAIL, error });
  }
}

export function* deleteSurveyFile(action: ReduxAction) {
  const { deletePayload } = action.sagaPayload;
  const { stepNumber, subStepId } = deletePayload;
  const steps = yield select(state => state.survey.surveyState);
  const surveyStepState = steps.filter(step => step.stepNumber === stepNumber);

  let itemToDelete;

  try {
    if (subStepId) {
      // Get the item to delete based on the selected file item id
      // Save the current item to delete
      itemToDelete = surveyStepState.find(stepItem => stepItem.id === subStepId) || {
        gcsBucketId: '',
        gcsItemId: '',
      };
    } else {
      // Get the item to delete based on the latest stack item
      // Save the current item to delete
      itemToDelete = surveyStepState[0];
    }

    const { gcsBucketId, gcsItemId, id } = itemToDelete;
    const activityId = yield select(state => state.survey.activity.id);

    yield call(UploadsController.deleteFile, {
      activityId,
      bucketId: gcsBucketId,
      fileId: gcsItemId,
    });

    yield put({
      type: SurveyActionTypes.DELETE_SURVEY_FILE.SUCCESS,
      payload: {
        itemId: id,
      },
    });
  } catch (error) {
    yield put({ type: SurveyActionTypes.DELETE_SURVEY_FILE.FAIL });
  }
}

export function* SiteSurveyControllerSaga() {
  yield takeEvery(SurveyActionTypes.FETCH_SURVEY_ACTIVITY_INFO.START, fetchSurveyActivityData);
  yield takeEvery(SurveyActionTypes.UPLOAD_SURVEY_FILE_INIT, uploadSiteFile);
  yield takeEvery(SurveyActionTypes.DELETE_SURVEY_FILE.START, deleteSurveyFile);
  yield takeEvery(SurveyActionTypes.SAVE_ADDITIONAL_SURVEY_TASK.START, saveAdditionaSurveylTask);
}
